import {
  type Task,
  type TaskUpdateBodyParams,
  type TaskCreateBodyParams,
  TaskStatus,
  TaskAssigneeType,
} from 'src/api/generated';
import {
  useUpdateTaskMutation,
  useCreateTaskMutation,
} from 'src/modules/shared/hooks/api/tasks/tasks.mutation';
import { useAlert } from 'src/contexts/alert-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { formatDateForAPI } from 'src/modules/shared/utils/formatting/date.util';
import { getAssigneeTypeEnum } from 'src/modules/shared/utils/tasks/assignee.util';

/**
 * Shared hook for common task operations used across modules
 */
export const useTaskOperations = () => {
  const { showAlert } = useAlert();
  const { updateTask, loading: updatingTask } = useUpdateTaskMutation();
  const { createTask, loading: creatingTask } = useCreateTaskMutation();
  const { user: currentUser } = useCurrentUser();

  const handleToggleComplete = async (task: Task) => {
    if (!task.id) {
      showAlert('No se pudo identificar la tarea', 'error');
      return;
    }

    const isFinished =
      task.status === TaskStatus.COMPLETED ||
      task.status === TaskStatus.CANCELLED;

    const newStatus = isFinished ? TaskStatus.SCHEDULED : TaskStatus.COMPLETED;

    const updateData: Partial<TaskUpdateBodyParams> = { status: newStatus };

    try {
      await updateTask({
        taskId: task.id,
        data: updateData,
      });
    } catch (error) {
      console.error('Error toggling task:', error);
      showAlert(
        `Ocurrió un error al ${isFinished ? 'marcar como pendiente' : 'completar'} la tarea`,
        'error'
      );
    }
  };

  const handleTaskUpdate = async (
    taskId: string | undefined,
    field: keyof TaskUpdateBodyParams,
    value: unknown
  ): Promise<void> => {
    if (!taskId) {
      showAlert('No se pudo identificar la tarea', 'error');
      return;
    }

    try {
      await updateTask({
        taskId,
        data: { [field]: value },
      });
    } catch (error) {
      console.error('Error updating task:', error);
      showAlert('Ocurrió un error al actualizar la tarea', 'error');
    }
  };

  const handleCancelTask = async (taskId: string | undefined) => {
    if (!taskId) {
      showAlert('No se pudo identificar la tarea', 'error');
      return;
    }

    try {
      await updateTask({
        taskId,
        data: {
          status: TaskStatus.CANCELLED,
        },
      });
    } catch (error) {
      console.error('Error cancelling task:', error);
      showAlert('Ocurrió un error al cancelar la tarea', 'error');
    }
  };

  const handleCreateTask = async (params: {
    objective: string;
    dueDate?: string | null;
    assignee: { type: 'user' | 'ai_employee'; id: string };
    conversationId?: number;
  }): Promise<void> => {
    if (!currentUser || !params.objective.trim()) {
      showAlert('Debe especificar el objetivo de la tarea', 'error');
      return;
    }

    const assignedToType = getAssigneeTypeEnum(params.assignee.type);

    const taskData: TaskCreateBodyParams = {
      objective: params.objective.trim(),
      dueDate: params.dueDate
        ? formatDateForAPI(new Date(params.dueDate))
        : undefined,
      assignedTo: {
        id: params.assignee.id,
        type: assignedToType,
      },
      createdBy: {
        id: String(currentUser.id),
        type: TaskAssigneeType.USER,
      },
      context: {
        conversationId: params.conversationId,
      },
      status: TaskStatus.SCHEDULED,
    };

    try {
      await createTask(taskData);
    } catch (error) {
      console.error('Error creating task:', error);
      showAlert('Ocurrió un error al crear la tarea', 'error');
    }
  };

  const getTooltipContent = (task: Task): string => {
    if (
      task.status === TaskStatus.COMPLETED ||
      task.status === TaskStatus.CANCELLED
    ) {
      return 'Marcar como pendiente';
    }

    return 'Marcar como completada';
  };

  const isTaskFinished = (task: Task): boolean =>
    task.status === TaskStatus.COMPLETED ||
    task.status === TaskStatus.CANCELLED;

  return {
    handleToggleComplete,
    handleTaskUpdate,
    handleCancelTask,
    handleCreateTask,
    getTooltipContent,
    isTaskFinished,
    updatingTask,
    creatingTask,
    currentUser,
  };
};
